import { useState, useEffect } from 'react'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import clsx from 'clsx'

interface PivotModalProps {
  open: boolean
  onClose: () => void
  orderIds: string[]
  csrfToken: string
  onError: (msg: string) => void
}

// Module-scope so React keeps a stable component identity across parent
// re-renders. Defining this inside PivotModal would remount each <input>
// on every state change — and lose the click event mid-flight.
function Check({
  checked, onChange, label, disabled,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <label className={clsx('flex cursor-pointer items-center gap-2 text-sm', disabled && 'cursor-not-allowed opacity-50')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800"
        data-testid={`pivot-check-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`}
      />
      <span>{label}</span>
    </label>
  )
}

// PivotModal — direct port of app/javascript/app/components/Vpo/PivotModal.jsx.
//
// Groups: UPC ⊕ (StyleNo / Color / Size) — UPC is mutually exclusive with
// the three triple-key fields. The legacy state machine clears the other
// side whenever the user toggles one.
//
// On Export, POSTs a JSON settings blob to /vpo/pivot_table.xlsx, receives
// a blob, downloads as `pivot_table_${DDMMYYYY_h-mm}.xlsx`.
export default function PivotModal({ open, onClose, orderIds, csrfToken, onError }: PivotModalProps) {
  const [groupByUpc, setGroupByUpc]         = useState(false)
  const [groupByStyleNo, setGroupByStyleNo] = useState(true)
  const [groupByColor, setGroupByColor]     = useState(false)
  const [groupBySize, setGroupBySize]       = useState(false)

  const [showCost, setShowCost]             = useState(true)
  const [showSelling, setShowSelling]       = useState(false)
  const [showCustomer, setShowCustomer]     = useState(false)
  const [showDescription, setShowDescription] = useState(false)
  const [showOrderNo, setShowOrderNo]       = useState(true)
  const [showBo, setShowBo]                 = useState(false)

  const [submitting, setSubmitting]         = useState(false)

  // Mutual exclusion — exact port of legacy lines 27–41.
  // UPC toggled ON  → clear StyleNo/Color/Size
  // UPC toggled OFF → restore StyleNo to its default (true)
  // StyleNo toggled at all → if UPC still on, clear UPC
  // Color/Size do NOT clear UPC in legacy — only `disabled` prevents the
  // click while UPC is on, so they stay false whenever UPC is on.
  useEffect(() => {
    if (groupByUpc) {
      setGroupByStyleNo(false)
      setGroupBySize(false)
      setGroupByColor(false)
    } else {
      setGroupByStyleNo(true)
    }
  }, [groupByUpc])
  useEffect(() => {
    if (groupByUpc) setGroupByUpc(false)
  }, [groupByStyleNo])

  const doExport = async () => {
    setSubmitting(true)
    try {
      const settings = {
        order_ids:        orderIds,
        group_by_upc:     groupByUpc,
        group_by_style_no: groupByStyleNo,
        group_by_color:   groupByColor,
        group_by_size:    groupBySize,
        show_cost:        showCost,
        show_selling:     showSelling,
        show_customer:    showCustomer,
        show_description: showDescription,
        show_order_no:    showOrderNo,
        show_bo:          showBo,
      }
      const res = await fetch('/vpo/pivot_table.xlsx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/xlsx',
        },
        body: JSON.stringify({ settings }),
      })
      if (!res.ok) {
        onError(`Pivot export failed (${res.status}).`)
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const now = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      a.download = `pivot_table_${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${now.getHours()}-${pad(now.getMinutes())}.xlsx`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50" data-testid="vpo-pivot-modal">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-xl rounded-lg border border-zinc-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
          <DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-white">Export Pivot Table</DialogTitle>
          <p className="mt-1 text-xs text-zinc-500">Generates an xlsx grouped by the keys you pick.</p>

          <div className="mt-4 grid grid-cols-2 gap-6">
            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Group By</legend>
              <div className="space-y-1.5" data-testid="pivot-group-by">
                <Check checked={groupByUpc}     onChange={setGroupByUpc}     label="UPC"     disabled={groupByStyleNo} />
                <Check checked={groupByStyleNo} onChange={setGroupByStyleNo} label="Style #" disabled={groupByUpc} />
                <Check checked={groupByColor}   onChange={setGroupByColor}   label="Color"   disabled={groupByUpc} />
                <Check checked={groupBySize}    onChange={setGroupBySize}    label="Size"    disabled={groupByUpc} />
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Show Info</legend>
              <div className="space-y-1.5" data-testid="pivot-show-info">
                <Check checked={showCost}        onChange={setShowCost}        label="Cost" />
                <Check checked={showSelling}     onChange={setShowSelling}     label="Selling" />
                <Check checked={showCustomer}    onChange={setShowCustomer}    label="Customer" />
                <Check checked={showDescription} onChange={setShowDescription} label="Description" />
                <Check checked={showOrderNo}     onChange={setShowOrderNo}     label="Order #" />
                <Check checked={showBo}          onChange={setShowBo}          label="BO" />
              </div>
            </fieldset>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800">
              Cancel
            </button>
            <button type="button" onClick={doExport} disabled={submitting} data-testid="vpo-pivot-export"
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
